import type { ReactNode } from 'react';

interface Block {
  kind: 'heading' | 'paragraph' | 'list';
  level?: number;
  lines: string[];
}

export function safeLink(value: string): string | null {
  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol === 'https:' || url.protocol === 'http:' || (url.origin === window.location.origin && url.protocol === window.location.protocol)) {
      return url.href;
    }
  } catch (_) {
    return null;
  }
  return null;
}

function inline(value: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^\s)]+\))/g;
  let offset = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(value))) {
    if (match.index > offset) {
      nodes.push(value.slice(offset, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={match.index}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={match.index}>{token.slice(1, -1)}</code>);
    } else {
      const parts = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      const href = parts ? safeLink(parts[2]) : null;
      nodes.push(href
        ? <a href={href} key={match.index} rel="noreferrer" target="_blank">{parts?.[1]}</a>
        : <span key={match.index}>{parts?.[1] || token}</span>);
    }
    offset = match.index + token.length;
  }
  if (offset < value.length) {
    nodes.push(value.slice(offset));
  }
  return nodes;
}

export function parseMarkdown(markdown: string): Block[] {
  const blocks: Block[] = [];
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n').slice(0, 2_000);
  let paragraph: string[] = [];
  let list: string[] = [];

  function flush() {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', lines: [paragraph.join(' ')] });
      paragraph = [];
    }
    if (list.length) {
      blocks.push({ kind: 'list', lines: list });
      list = [];
    }
  }

  lines.forEach((line) => {
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    const bullet = line.match(/^\s*[-*]\s+(.+)$/);
    if (heading) {
      flush();
      blocks.push({ kind: 'heading', level: heading[1].length, lines: [heading[2]] });
    } else if (bullet) {
      if (paragraph.length) flush();
      list.push(bullet[1]);
    } else if (!line.trim()) {
      flush();
    } else {
      if (list.length) flush();
      paragraph.push(line.trim());
    }
  });
  flush();
  return blocks;
}

export function SafeMarkdown({ markdown }: { markdown: string }) {
  return (
    <div className="markdown">
      {parseMarkdown(markdown).map((block, index) => {
        if (block.kind === 'list') {
          return <ul key={index}>{block.lines.map((line, item) => <li key={item}>{inline(line)}</li>)}</ul>;
        }
        if (block.kind === 'heading') {
          const Heading = (`h${Math.min(block.level || 2, 4)}`) as keyof JSX.IntrinsicElements;
          return <Heading key={index}>{inline(block.lines[0])}</Heading>;
        }
        return <p key={index}>{inline(block.lines[0])}</p>;
      })}
    </div>
  );
}
